import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { Clock, MonitorSmartphone, Volume2, LogOut } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";

export default function StudentDashboard() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [data, setData] = useState(null);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    api.get("/student/dashboard").then((res) => setData(res.data));
  }, []);

  useEffect(() => {
    if (!data || data.session_status !== "submitted" || data.report_status === "READY") return;
    const interval = setInterval(() => {
      api.get("/student/dashboard").then((res) => setData(res.data));
    }, 5000);
    return () => clearInterval(interval);
  }, [data]);

  const handleStart = async () => {
    setStarting(true);
    try {
      await api.post("/student/assessment/start");
      navigate("/assessment");
    } catch (err) {
      toast.error("Could not start assessment");
    } finally {
      setStarting(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  if (!data) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>;

  const licenseStatus = data.license?.status;
  const sessionStatus = data.session_status;
  const reportStatus = data.report_status;
  const firstName = data.profile?.first_name;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="student-dashboard-page">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BrandLogo className="h-8" />
          </div>
          <Button variant="ghost" size="icon" data-testid="student-logout-button" onClick={handleLogout}><LogOut className="h-4 w-4" /></Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-16">
        {firstName && (
          <p className="text-center text-sm text-slate-400 mb-6" data-testid="student-dashboard-greeting">
            Welcome back, <span className="font-semibold text-slate-600">{firstName}</span>
          </p>
        )}
        {(!licenseStatus || licenseStatus === "NOT_ISSUED") && (
          <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center" data-testid="awaiting-activation-card">
            <h1 className="font-heading text-2xl font-bold text-slate-900 mb-3">CareerStudy360 Assessment</h1>
            <span className="inline-block rounded-full bg-amber-100 text-amber-700 text-xs font-medium px-4 py-1 mb-6">Awaiting Activation</span>
            <p className="text-slate-500 max-w-md mx-auto">
              Your CareerStudy360 profile is ready. Your assessment will become available once your assessment access is activated.
            </p>
          </div>
        )}

        {licenseStatus === "PENDING" && (
          <div className="bg-white rounded-2xl border border-rose-200 p-10 text-center" data-testid="license-blocked-card">
            <h1 className="font-heading text-2xl font-bold text-slate-900 mb-3">CareerStudy360 Assessment</h1>
            <span className="inline-block rounded-full bg-rose-100 text-rose-700 text-xs font-medium px-4 py-1 mb-6">Blocked — License Pool Exhausted</span>
            <p className="text-slate-500 max-w-md mx-auto">
              {data.profile?.license_blocked_reason || "Your marketing executive's license pool needs a top-up. Please contact them, or wait for admin approval — your access will unlock automatically once resolved."}
            </p>
          </div>
        )}

        {licenseStatus === "ACTIVE" && !sessionStatus && (
          <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center" data-testid="ready-to-start-card">
            <h1 className="font-heading text-2xl font-bold text-slate-900 mb-3">
              {firstName ? `${firstName}, Your CareerStudy360 Assessment Is Ready` : "Your CareerStudy360 Assessment Is Ready"}
            </h1>
            <p className="text-slate-500 max-w-md mx-auto mb-8">
              Take your time and answer honestly — there are no right or wrong answers.
            </p>
            <div className="flex justify-center gap-8 text-sm text-slate-500 mb-8">
              <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-indigo-500" /> ~35-45 minutes</div>
              <div className="flex items-center gap-2"><MonitorSmartphone className="h-4 w-4 text-indigo-500" /> Laptop / Desktop / Tablet</div>
              <div className="flex items-center gap-2"><Volume2 className="h-4 w-4 text-indigo-500" /> Quiet environment</div>
            </div>
            <Button data-testid="start-assessment-cta-button" disabled={starting} onClick={handleStart} className="h-12 px-10 rounded-full bg-indigo-600 hover:bg-indigo-700">
              Start Assessment
            </Button>
          </div>
        )}

        {sessionStatus === "in_progress" && (
          <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center" data-testid="resume-assessment-card">
            <h1 className="font-heading text-2xl font-bold text-slate-900 mb-3">
              {firstName ? `Resume Your Assessment, ${firstName}` : "Resume Your Assessment"}
            </h1>
            <p className="text-slate-500 mb-8">Your progress has been saved automatically.</p>
            <Button data-testid="resume-assessment-button" onClick={() => navigate("/assessment")} className="h-12 px-10 rounded-full bg-indigo-600 hover:bg-indigo-700">
              Resume Assessment
            </Button>
          </div>
        )}

        {sessionStatus === "submitted" && reportStatus !== "READY" && (
          <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center" data-testid="report-preparing-card">
            <h1 className="font-heading text-2xl font-bold text-slate-900 mb-3">Assessment Successfully Completed</h1>
            <p className="text-slate-500 max-w-md mx-auto">
              {firstName ? `Thank you, ${firstName}. Your responses have` : "Your responses have"} been successfully captured and are now being evaluated. CareerStudy360 is preparing
              your detailed personality, strengths and career-alignment report. Reports are normally prepared within
              approximately one hour.
            </p>
          </div>
        )}

        {reportStatus === "READY" && (
          <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center" data-testid="report-ready-card">
            <h1 className="font-heading text-2xl font-bold text-slate-900 mb-3">
              {firstName ? `${firstName}, Your CareerStudy360 Report Is Ready` : "Your CareerStudy360 Report Is Ready"}
            </h1>
            <p className="text-slate-500 mb-8">Discover your personality, strengths and career-alignment insights.</p>
            <Button data-testid="view-report-button" onClick={() => navigate("/report")} className="h-12 px-10 rounded-full bg-indigo-600 hover:bg-indigo-700">
              View My Report
            </Button>
            {data.next_eligible_assessment_at && (
              <p className="text-xs text-slate-400 mt-6" data-testid="next-eligible-date">
                Next eligible reassessment date: {new Date(data.next_eligible_assessment_at).toLocaleDateString()}
              </p>
            )}
          </div>
        )}

        {licenseStatus === "ACTIVE" && (
          <div className="mt-8 bg-white rounded-2xl border border-violet-200 p-8 text-center" data-testid="aptitude-test-card">
            <span className="inline-block rounded-full bg-violet-100 text-violet-700 text-xs font-medium px-4 py-1 mb-4">Supplementary Module</span>
            <h2 className="font-heading text-xl font-bold text-slate-900 mb-2">The Aptitude Test</h2>
            <p className="text-slate-500 max-w-md mx-auto mb-6 text-sm">
              8 short, performance-based reasoning exercises that reinforce your CareerStudy360 report. Optional, ~15-20 minutes.
            </p>
            <Button data-testid="go-to-aptitude-test-button" variant="outline" onClick={() => navigate("/aptitude")} className="rounded-full">
              Open Aptitude Test
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}
